import { ReactElement } from 'react';
import { Typography, Button, Tag, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import canGoBack from '../../../../router/canGoBack';
import { styled } from 'styled-components';
import { blockStyle } from '../../../../components/styles/Block';
import useRecordingDetail from '../hooks/useRecordingDetail';
import isAudioExpired from '../utils/isAudioExpired';
import DeleteButton from './DeleteButton';
import RetryButton from './RetryButton';

const { Title } = Typography;

const HeaderBlock = styled.header`
  ${blockStyle}
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderActions = styled.div`
  margin-left: auto;
  display: flex;
  gap: 4px;
`;

// ID定義は backend/prisma/seed/recordingStatus.ts / recordingSource.ts を参照
const RECORDING_STATUS = {
  PROCESSING: 1,
  COMPLETED: 2,
  FAILED: 3,
} as const;

const FLEX_D_PLAYER_SOURCE_ID = 1;

function getStatusBadge(statusId: number, statusName: string): ReactElement {
  switch (statusId) {
    case RECORDING_STATUS.PROCESSING:
      return <Tag color='gold'>{statusName}</Tag>;
    case RECORDING_STATUS.COMPLETED:
      return <Tag color='green'>{statusName}</Tag>;
    case RECORDING_STATUS.FAILED:
      return <Tag color='red'>{statusName}</Tag>;
    default:
      return <Tag>{statusName}</Tag>;
  }
}

const Header = (): ReactElement => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const recordingId = Number(id);
  const [messageApi, contextHolder] = message.useMessage();
  const { recording, retryRecording } = useRecordingDetail(recordingId, messageApi);

  const showRetryButton =
    recording.recordingStatusId === RECORDING_STATUS.FAILED &&
    recording.sourceId === FLEX_D_PLAYER_SOURCE_ID &&
    !isAudioExpired(recording.registeredAt);

  return (
    <HeaderBlock>
      {contextHolder}
      <Button
        type='text'
        icon={<ArrowLeftOutlined />}
        onClick={() => {
          if (canGoBack()) {
            void navigate(-1);
          } else {
            void navigate('/recordings');
          }
        }}
      />
      <Title level={4} style={{ margin: 0 }}>
        {recording.title}
      </Title>
      {getStatusBadge(recording.recordingStatusId, recording.recordingStatusName)}
      <HeaderActions>
        {showRetryButton && (
          <RetryButton duration={recording.duration} onRetry={retryRecording} messageApi={messageApi} />
        )}
        <DeleteButton recordingId={recordingId} />
      </HeaderActions>
    </HeaderBlock>
  );
};

export default Header;
